import * as ExcelJS from "exceljs";
import { addJustupBranding, setJustupMetadata } from "../../../../shared/utils/excel-branding.util";

const C = {
  headerBg: "FF1B2A4A",
  headerFont: "FFFFFFFF",
  periodBg: "FFE8EDF3",
  periodFont: "FF1B2A4A",
  clientBg: "FFF5F7FA",
  entryFont: "FF667085",
  entryBorder: "FFE4E7EC",
  border: "FFD0D5DD",
  statGreen: "FFE6FFF5",
  statPurple: "FFF0E6FF",
  statBlue: "FFE0EAFF",
  statOrange: "FFFFF0E0",
  titleColor: "FF1B2A4A",
  up: "FF16A34A",
  down: "FFDC2626",
};

interface ClientEntry {
  date: string;
  dateLabel: string;
  montant: number;
  factureIds: string[];
  commandeIds: string[];
}

interface ClientRow {
  denomination: string;
  ca: number;
  partPeriod: number;
  nbEntries: number;
  entries: ClientEntry[];
}

interface Row {
  label: string;
  ca: number;
  cumul: number;
  variationPct: number | null;
  partPct: number;
  clients: ClientRow[];
  nbClients: number;
}

interface Stats {
  total: number;
  totalFormatted: string;
  avg: number;
  avgFormatted: string;
  bestLabel: string;
  bestCA: number;
  growthLabel: string;
  growthPct: number;
  count: number;
}

export interface CaCumuleExcelData {
  rows: Row[];
  stats: Stats;
  scaleLabel: string;
  scaleLabelSingularLower: string;
  timeScaleLabel: string;
}

function colLetter(col: number): string {
  let s = "";
  let c = col;
  while (c > 0) {
    c--;
    s = String.fromCharCode(65 + (c % 26)) + s;
    c = Math.floor(c / 26);
  }
  return s;
}

function cellRef(col: number, row: number): string {
  return `${colLetter(col)}${row}`;
}

function headerStyle(cell: ExcelJS.Cell): void {
  cell.font = { bold: true, size: 10, color: { argb: C.headerFont } };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.headerBg } };
  cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  cell.border = { bottom: { style: "medium", color: { argb: C.headerBg } } };
}

function statStyle(cell: ExcelJS.Cell, bg: string): void {
  cell.font = { bold: true, size: 11 };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
  cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  const b = { style: "thin" as const, color: { argb: C.border } };
  cell.border = { top: b, bottom: b, left: b, right: b };
}

function periodStyle(cell: ExcelJS.Cell): void {
  cell.font = { bold: true, size: 10, color: { argb: C.periodFont } };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.periodBg } };
  cell.border = {
    bottom: { style: "thin", color: { argb: C.border } },
    top: { style: "thin", color: { argb: C.border } },
  };
}

function clientStyle(cell: ExcelJS.Cell): void {
  cell.font = { size: 9, italic: true };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.clientBg } };
  cell.border = { bottom: { style: "hair", color: { argb: C.border } } };
}

function entryStyle(cell: ExcelJS.Cell): void {
  cell.font = { size: 9, color: { argb: C.entryFont } };
  cell.border = { bottom: { style: "hair", color: { argb: C.entryBorder } } };
}

const COL = { period: 1, client: 2, date: 3, factures: 4, commandes: 5, montant: 6, cumul: 7, variation: 8, pct: 9 };
const COL_WIDTHS = [22, 28, 14, 24, 24, 16, 18, 12, 8];
const HEADERS = ["Période", "Client", "Date", "N° Factures", "N° Commandes", "Montant (€)", "Cumulé (€)", "Δ vs précéd.", "%"];
const HEADER_ROW = 6;
const LAST_COL = COL_WIDTHS.length;

export async function exportCaCumuleExcel(
  data: CaCumuleExcelData,
): Promise<void> {
  const wb = new ExcelJS.Workbook();
  setJustupMetadata(wb);

  const ws = wb.addWorksheet("CA cumulé", {
    views: [{ state: "frozen", ySplit: HEADER_ROW, showGridLines: false }],
  });

  COL_WIDTHS.forEach((w, i) => (ws.getColumn(i + 1).width = w));

  // ── Title ──
  ws.mergeCells(1, 1, 1, LAST_COL - 1);
  const title = ws.getCell(1, 1);
  title.value = `CA cumulé par ${data.scaleLabelSingularLower} — ${data.timeScaleLabel}`;
  title.font = { bold: true, size: 16, color: { argb: C.titleColor } };
  ws.getRow(1).height = 30;

  // ── Stats row 3 ──
  ws.mergeCells(3, 1, 3, 3);
  ws.mergeCells(3, 4, 3, 6);
  ws.mergeCells(3, 7, 3, 9);
  ws.getCell(3, 1).value = `${data.stats.count} ${data.scaleLabelSingularLower}${data.stats.count > 1 ? "s" : ""} · Total ${data.stats.totalFormatted}`;
  statStyle(ws.getCell(3, 1), C.statPurple);
  ws.getCell(3, 4).value = `Moyenne / ${data.scaleLabelSingularLower} : ${data.stats.avgFormatted}`;
  statStyle(ws.getCell(3, 4), C.statBlue);
  ws.getCell(3, 7).value = `Progression : ${data.stats.growthLabel}`;
  statStyle(
    ws.getCell(3, 7),
    data.stats.growthPct > 0 ? C.statGreen : data.stats.growthPct < 0 ? "FFFEE2E2" : C.statOrange,
  );
  ws.getRow(3).height = 26;

  // ── Best period row 4 ──
  ws.mergeCells(4, 1, 4, LAST_COL);
  const bestCell = ws.getCell(4, 1);
  bestCell.value = `🏆  Meilleur${/e$/.test(data.scaleLabelSingularLower) ? "e" : ""} ${data.scaleLabelSingularLower} : ${data.stats.bestLabel} — ${data.stats.bestCA.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`;
  bestCell.font = { bold: true, size: 10, color: { argb: C.titleColor }, italic: true };
  bestCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.statGreen } };
  bestCell.alignment = { horizontal: "left", vertical: "middle", indent: 1 };
  ws.getRow(4).height = 22;
  ws.getRow(5).height = 8;

  // ── Headers ──
  HEADERS.forEach((h, i) => {
    const c = ws.getCell(HEADER_ROW, i + 1);
    c.value = h;
    headerStyle(c);
  });
  ws.getRow(HEADER_ROW).height = 22;
  ws.autoFilter = {
    from: { row: HEADER_ROW, column: 1 },
    to: { row: HEADER_ROW, column: LAST_COL },
  };

  // ── Data rows: Period → Client → Entry ──
  const eurFmt = '#,##0\\ "€"';
  let row = HEADER_ROW;
  const periodRowNumbers: number[] = [];

  for (const period of data.rows) {
    const periodRowNum = ++row;
    periodRowNumbers.push(periodRowNum);

    const clientTotalCells: string[] = [];

    for (const client of period.clients) {
      const clientRowNum = ++row;
      const entryTotalCells: string[] = [];

      if (client.entries.length > 0) {
        for (const entry of client.entries) {
          const r = ++row;
          const wsRow = ws.getRow(r);
          wsRow.getCell(COL.date).value = entry.dateLabel;
          wsRow.getCell(COL.factures).value = entry.factureIds.join(", ") || "";
          wsRow.getCell(COL.commandes).value = entry.commandeIds.join(", ") || "";
          wsRow.getCell(COL.montant).value = entry.montant;
          wsRow.getCell(COL.montant).numFmt = eurFmt;
          wsRow.getCell(COL.montant).alignment = { horizontal: "right" };
          for (let c = 1; c <= LAST_COL; c++) entryStyle(wsRow.getCell(c));
          wsRow.getCell(COL.factures).alignment = { wrapText: true };
          wsRow.getCell(COL.commandes).alignment = { wrapText: true };
          entryTotalCells.push(cellRef(COL.montant, r));
        }
      }

      // Client row
      const cRow = ws.getRow(clientRowNum);
      cRow.getCell(COL.client).value = client.denomination;
      if (entryTotalCells.length > 0) {
        cRow.getCell(COL.montant).value = {
          formula: `SUM(${entryTotalCells.join(",")})`,
          result: client.ca,
        } as any;
      } else {
        cRow.getCell(COL.montant).value = client.ca;
      }
      cRow.getCell(COL.montant).numFmt = eurFmt;
      cRow.getCell(COL.montant).alignment = { horizontal: "right" };
      for (let c = 1; c <= LAST_COL; c++) clientStyle(cRow.getCell(c));
      clientTotalCells.push(cellRef(COL.montant, clientRowNum));
    }

    // Period row
    const pRow = ws.getRow(periodRowNum);
    pRow.getCell(COL.period).value = period.label;
    if (clientTotalCells.length > 0) {
      pRow.getCell(COL.montant).value = {
        formula: `SUM(${clientTotalCells.join(",")})`,
        result: period.ca,
      } as any;
    } else {
      pRow.getCell(COL.montant).value = period.ca;
    }
    pRow.getCell(COL.montant).numFmt = eurFmt;
    pRow.getCell(COL.montant).alignment = { horizontal: "right" };

    // Cumul
    pRow.getCell(COL.cumul).value = period.cumul;
    pRow.getCell(COL.cumul).numFmt = eurFmt;
    pRow.getCell(COL.cumul).alignment = { horizontal: "right" };

    // Variation
    if (period.variationPct !== null && isFinite(period.variationPct)) {
      pRow.getCell(COL.variation).value = period.variationPct / 100;
      pRow.getCell(COL.variation).numFmt = "+0.0%;-0.0%;0.0%";
      pRow.getCell(COL.variation).font = {
        bold: true, size: 10,
        color: {
          argb: period.variationPct > 0.5 ? C.up : period.variationPct < -0.5 ? C.down : "FF64748B",
        },
      };
    } else {
      pRow.getCell(COL.variation).value = "—";
    }
    pRow.getCell(COL.variation).alignment = { horizontal: "right" };

    for (let c = 1; c <= LAST_COL; c++) periodStyle(pRow.getCell(c));
    // Re-apply variation font after periodStyle
    if (period.variationPct !== null && isFinite(period.variationPct)) {
      pRow.getCell(COL.variation).font = {
        bold: true, size: 10,
        color: {
          argb: period.variationPct > 0.5 ? C.up : period.variationPct < -0.5 ? C.down : "FF64748B",
        },
      };
    }
  }

  // ── Grand total ──
  row += 2;
  const gtRow = row;
  ws.mergeCells(gtRow, 1, gtRow, COL.montant - 1);
  const gtLabel = ws.getCell(gtRow, 1);
  gtLabel.value = "TOTAL GÉNÉRAL";
  gtLabel.font = { bold: true, size: 11, color: { argb: C.headerFont } };
  gtLabel.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.headerBg } };
  gtLabel.alignment = { horizontal: "right", vertical: "middle" };

  const gtCell = ws.getCell(gtRow, COL.montant);
  if (periodRowNumbers.length > 0) {
    const refs = periodRowNumbers.map((r) => cellRef(COL.montant, r)).join(",");
    gtCell.value = { formula: `SUM(${refs})`, result: data.stats.total } as any;
  } else {
    gtCell.value = data.stats.total;
  }
  gtCell.font = { bold: true, size: 11, color: { argb: C.headerFont } };
  gtCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.headerBg } };
  gtCell.numFmt = eurFmt;

  const gtPct = ws.getCell(gtRow, COL.pct);
  gtPct.value = 1;
  gtPct.font = { bold: true, size: 11, color: { argb: C.headerFont } };
  gtPct.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.headerBg } };
  gtPct.numFmt = "0.0%";
  ws.getRow(gtRow).height = 26;

  const gtRef = cellRef(COL.montant, gtRow);

  // ── Fill % formulas for period rows ──
  for (const pRowNum of periodRowNumbers) {
    ws.getCell(pRowNum, COL.pct).value = {
      formula: `IF(${gtRef}=0,0,${cellRef(COL.montant, pRowNum)}/${gtRef})`,
      result: data.stats.total > 0 ? (ws.getCell(pRowNum, COL.montant).value as any)?.result / data.stats.total : 0,
    } as any;
    ws.getCell(pRowNum, COL.pct).numFmt = "0.0%";
    periodStyle(ws.getCell(pRowNum, COL.pct));
  }

  // ── Onglet "Données brutes" — 1 ligne par entrée, prêt pour TCD ──
  addRawDataSheet(wb, data);

  // ── Branding + download ──
  await addJustupBranding(wb, ws, LAST_COL);

  const buffer = (await wb.xlsx.writeBuffer()) as ArrayBuffer;
  const blob = new Blob([new Uint8Array(buffer)], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const filename = `ca-cumule-${data.scaleLabelSingularLower}-${new Date().toISOString().slice(0, 10)}.xlsx`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 10_000);
}

function fmtDateFR(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
}

function rawHeaderStyle(cell: ExcelJS.Cell): void {
  cell.font = { bold: true, size: 10, color: { argb: C.headerFont } };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.headerBg } };
  cell.alignment = { horizontal: "center", vertical: "middle" };
}

function addRawDataSheet(wb: ExcelJS.Workbook, data: CaCumuleExcelData): void {
  const RAW_HEADERS = ["Période", "Client", "Date", "N° Factures", "N° Commandes", "Montant HT"];
  const RAW_WIDTHS = [20, 28, 14, 28, 28, 16];

  const ws = wb.addWorksheet("Données brutes", {
    views: [{ state: "frozen", ySplit: 1, showGridLines: true }],
  });

  RAW_WIDTHS.forEach((w, i) => (ws.getColumn(i + 1).width = w));

  RAW_HEADERS.forEach((h, i) => {
    const c = ws.getCell(1, i + 1);
    c.value = h;
    rawHeaderStyle(c);
  });
  ws.getRow(1).height = 22;
  ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: RAW_HEADERS.length } };

  const eurFmt = '#,##0.00\\ "€"';
  let r = 1;
  for (const period of data.rows) {
    for (const client of period.clients) {
      for (const entry of client.entries) {
        r++;
        const row = ws.getRow(r);
        row.getCell(1).value = period.label;
        row.getCell(2).value = client.denomination;
        row.getCell(3).value = fmtDateFR(entry.date);
        row.getCell(4).value = entry.factureIds.join(", ") || "";
        row.getCell(5).value = entry.commandeIds.join(", ") || "";
        row.getCell(6).value = entry.montant;
        row.getCell(6).numFmt = eurFmt;
        row.getCell(6).alignment = { horizontal: "right" };
      }
    }
  }
}
